import React, { useState, useContext, useEffect } from 'react';
import { fmtDate as _fmtDate, fmtDateTime as _fmtDateTime } from '@/lib/dates';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertTriangle, Trash2, Eye, RefreshCw, Edit } from 'lucide-react';

const IncidentReportManagement = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/incident-reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data);
    } catch (error) {
      toast.error('Failed to load incident reports');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const deleteReport = async (reportId) => {
    setReportToDelete(reportId);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!reportToDelete) return;
    
    try {
      console.log(`Deleting incident report ID: ${reportToDelete}`);
      await axios.delete(`${API}/incident-reports/${reportToDelete}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Incident report deleted successfully');
      setShowDeleteDialog(false);
      setReportToDelete(null);
      fetchReports();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete report');
      setShowDeleteDialog(false);
      setReportToDelete(null);
    }
  };

  const viewReport = (report) => {
    setSelectedReport(report);
  };

  const editReport = (report) => {
    navigate('/field/incident-report', { state: { editData: report } });
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'Low': 'bg-green-100 text-green-800',
      'Medium': 'bg-yellow-100 text-yellow-800',
      'High': 'bg-orange-100 text-orange-800',
      'Critical': 'bg-red-100 text-red-800'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      
      
      <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <AlertTriangle className="h-6 w-6 text-orange-600" />
          <h2 className="text-2xl font-bold">Incident Reports Management</h2>
        </div>
        <Button onClick={fetchReports} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="mb-4">
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold" style={{ color: '#6B9F3E' }}>{reports.length}</p>
                <p className="text-sm text-gray-500">Total Reports</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{reports.filter(r => r.severity === 'Critical').length}</p>
                <p className="text-sm text-gray-500">Critical</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-600">{reports.filter(r => r.severity === 'High').length}</p>
                <p className="text-sm text-gray-500">High</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-600">{reports.filter(r => r.witnesses && r.witnesses.length > 0).length}</p>
                <p className="text-sm text-gray-500">With Witnesses</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {reports.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500">No incident reports found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => (
            <Card key={report.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg mb-2">{report.incident_type}</CardTitle>
                    <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                      <span><strong>Location:</strong> {report.location_of_incident}</span>
                      <span>•</span>
                      <span><strong>Date:</strong> {_fmtDate(report.date_occurred)} {report.time_occurred}</span>
                      <span>•</span>
                      <span><strong>Reporter:</strong> {report.reported_by}</span>
                    </div>
                  </div>
                  <Badge className={getSeverityColor(report.severity)}>
                    {report.severity}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <p className="text-sm text-gray-700 mb-2"><strong>Description:</strong></p>
                  <p className="text-sm text-gray-600">{report.description}</p>
                </div>
                {report.witnesses && report.witnesses.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-700 mb-1"><strong>Witnesses ({report.witnesses.length}):</strong></p>
                    <div className="space-y-1">
                      {report.witnesses.map((witness, idx) => (
                        <p key={idx} className="text-sm text-gray-600 pl-4">• {witness}</p>
                      ))}
                    </div>
                  </div>
                )}
                {report.injuries_reported && (
                  <div className="mb-4">
                    <Badge variant="destructive">Injuries Reported</Badge>
                  </div>
                )}
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-xs text-gray-500">
                    <span>Submitted: {_fmtDateTime(report.submitted_at)}</span>
                    {report.photos && report.photos.length > 0 && (
                      <span className="ml-4">📸 {report.photos.length} photos</span>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        viewReport(report);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        editReport(report);
                      }}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteReport(report.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to delete this incident report? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default IncidentReportManagement;
